import {useMemo, useState} from "react";
import {ObjectComboMapping} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UsePermissionDataTableFilterProps = Omit<UseDataTableFilterProps<ObjectComboMapping>, 'filterConfig'>

export function useObjectComboMappingDataTableFilter(props: UsePermissionDataTableFilterProps): UseDataTableFilterResult<ObjectComboMapping> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [leftObjectNameQuery, setLeftObjectNameQuery] = useState<string | undefined>(undefined);
  const [leftObjectNameDebouncedQuery] = useDebouncedValue(leftObjectNameQuery, 200);

  const [rightObjectNameQuery, setRightObjectNameQuery] = useState<string | undefined>(undefined);
  const [rightObjectNameDebouncedQuery] = useDebouncedValue(rightObjectNameQuery, 200);

  const [keyQuery, setKeyQuery] = useState<string | undefined>(undefined);
  const [keyDebouncedQuery] = useDebouncedValue(rightObjectNameQuery, 200);

  const [valueQuery, setValueQuery] = useState<string | undefined>(undefined);
  const [valueDebouncedQuery] = useDebouncedValue(valueQuery, 200);

  const filterConfig: DataTableFilterConfig<ObjectComboMapping> = useMemo(() => {
    return {
      "leftObjectName": {
        setQuery: setLeftObjectNameQuery,
        query: leftObjectNameQuery,
        debouncedQuery: leftObjectNameDebouncedQuery,
        filter: ({leftObjectName}, query) => !leftObjectName || !leftObjectName.toLowerCase().includes(query.toLowerCase())
      },
      "rightObjectName": {
        setQuery: setRightObjectNameQuery,
        query: rightObjectNameQuery,
        debouncedQuery: rightObjectNameDebouncedQuery,
        filter: ({rightObjectName}, query) => !rightObjectName || !rightObjectName.toLowerCase().includes(query.toLowerCase())
      },
      "key": {
        setQuery: setKeyQuery,
        query: keyQuery,
        debouncedQuery: keyDebouncedQuery,
        filter: ({key}, query) => !key || !key.toLowerCase().includes(query.toLowerCase())
      },
      "value": {
        setQuery: setValueQuery,
        query: valueQuery,
        debouncedQuery: valueDebouncedQuery,
        filter: ({value}, query) => !value || !value.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    leftObjectNameQuery,
    leftObjectNameDebouncedQuery,
    rightObjectNameQuery,
    rightObjectNameDebouncedQuery,
    keyQuery,
    keyDebouncedQuery,
    valueQuery,
    valueDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
